import * as React from 'react';
import ErrorImage from './ErrorImage';
import { logError } from '@zoopla/logger';
import {
    heroContentStyle,
    heroContentWrapperStyle,
    heroGridStyle,
    heroImageWrapperStyle,
    heroWrapperStyle,
    imgStyle
} from './ErrorBoundary.styles.css';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes';

type Props = {
    children: React.ReactNode;
    renderOnError?: React.ReactNode | null;
};

type State = {
    hasError: boolean;
};

class ErrorBoundary extends React.Component<Props, State> {
    override state = { hasError: false };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        logError(error, errorInfo);
    }

    override render() {
        const hasDefinedOrNullRenderOnError =
            this.props.renderOnError || this.props.renderOnError === null;

        if (this.state.hasError) {
            return hasDefinedOrNullRenderOnError ? (
                this.props.renderOnError
            ) : (
                <section className={heroWrapperStyle}>
                    <div className={heroGridStyle}>
                        <div className={heroImageWrapperStyle}>
                            <ErrorImage className={imgStyle} />
                        </div>
                        <div className={heroContentStyle}>
                            <div className={heroContentWrapperStyle}>
                                <Box pb="S400">
                                    <h1 className={fontGroups.teapot}>
                                        Something went wrong
                                    </h1>
                                </Box>
                                <p>
                                    Our team has been made aware and are working
                                    to find a solution to this problem. Please
                                    refresh your page or, if the issue
                                    continues, please try again later.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>
            );
        }
        return this.props.children;
    }
}
export default ErrorBoundary;

export function withErrorBoundary<P extends JSX.IntrinsicAttributes>(
    WrappedComponent: React.ComponentType<React.PropsWithChildren<P>>,
    renderOnError: React.ReactNode
): React.ComponentType<React.PropsWithChildren<P>> {
    return (props: P) => (
        <ErrorBoundary renderOnError={renderOnError}>
            <WrappedComponent {...props} />
        </ErrorBoundary>
    );
}
